import { resolveBranchRef } from './branchResolver';

/**
 * When several `course.batches` rows share the same batchId (multi-branch),
 * prefer `enrollment.branchId`, then `enrollment.batchIndex`, over the first
 * matching row.
 */
export const pickBatchForEnrollment = (batches, enrollment) => {
  const list = Array.isArray(batches) ? batches : [];
  const candidates = list
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item, index }) =>
        (enrollment.batchIndex != null &&
          String(index) === String(enrollment.batchIndex)) ||
        String(item.batchId) === String(enrollment.batchId) ||
        String(index) === String(enrollment.batchId) ||
        String(item?.id) === String(enrollment.batchId),
    );
  if (!candidates.length) return undefined;
  if (candidates.length === 1) return candidates[0].item;

  if (enrollment.branchId) {
    const byBranch = candidates.find(
      ({ item }) =>
        item.branchId && String(item.branchId) === String(enrollment.branchId),
    );
    if (byBranch) return byBranch.item;
  }
  if (enrollment.batchIndex != null) {
    const byIndex = candidates.find(
      ({ index }) => String(index) === String(enrollment.batchIndex),
    );
    if (byIndex) return byIndex.item;
  }
  return candidates[0].item;
};

export const buildAcademyStudentId = (userId, createdAt) => {
  const seedDate = createdAt ? new Date(createdAt) : new Date();
  const yy = String(seedDate.getFullYear()).slice(-2);
  const mm = String(seedDate.getMonth() + 1).padStart(2, '0');
  const dd = String(seedDate.getDate()).padStart(2, '0');
  const head = String(userId || 'STUDENT').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const code = `${head.slice(0, 3)}${head.slice(-3)}`.padEnd(6, 'X').slice(0, 6);
  return `BRS-${yy}${mm}${dd}-${code}`;
};

export const getStudentDisplayName = (user, student) => {
  const personal = student?.personalDetails || student || {};
  const firstName = personal.firstName || user?.firstName || '';
  const lastName = personal.lastName || user?.lastName || '';
  return `${firstName} ${lastName}`.trim() || user?.displayName || 'Student';
};

export const getStudentRegistrationDate = (user, student) =>
  student?.registrationDate ||
  student?.createdAt ||
  student?.updatedAt ||
  user?.createdAt ||
  null;

export const getBranchFromCourses = (
  { student, enrollments = [], courses = {} },
  branchCatalogs = {},
) => {
  const directBranch =
    student?.branch ||
    student?.personalDetails?.branch ||
    student?.publicProfile?.branch;
  if (directBranch) {
    return resolveBranchRef(directBranch, branchCatalogs)?.name || directBranch;
  }

  const courseList = Array.isArray(courses) ? courses : Object.values(courses || {});
  const prioritized = [...enrollments].sort((a, b) => {
    const aDate = new Date(a.enrollmentDate || 0).getTime();
    const bDate = new Date(b.enrollmentDate || 0).getTime();
    return bDate - aDate;
  });

  for (const enrollment of prioritized) {
    const course =
      (Array.isArray(courses) ? null : courses[enrollment.courseId]) ||
      courseList.find((item) => String(item?.id) === String(enrollment.courseId));
    const batch = pickBatchForEnrollment(course?.batches, enrollment);
    const resolved = batch ? resolveBranchRef(batch, branchCatalogs) : null;
    if (resolved?.name) return resolved.name;
    if (course?.branch) {
      return resolveBranchRef(course.branch, branchCatalogs)?.name || course.branch;
    }
  }

  for (const course of courseList) {
    const firstBatchWithBranch = (course?.batches || []).find(
      (batch) => batch?.branch || batch?.branchId,
    );
    if (firstBatchWithBranch) {
      const resolved = resolveBranchRef(firstBatchWithBranch, branchCatalogs);
      if (resolved?.name) return resolved.name;
    }
    if (course?.branch) {
      return resolveBranchRef(course.branch, branchCatalogs)?.name || course.branch;
    }
  }

  return 'Main Branch';
};

export const buildStudentQrResolverUrl = (academyStudentId) =>
  `${window.location.origin}/student-pass/${encodeURIComponent(academyStudentId)}`;

export const buildStudentPortfolioUrl = (academyStudentId) =>
  `${window.location.origin}/students/portfolio/${encodeURIComponent(academyStudentId)}`;

export const resolvePortfolioCourses = ({ enrollments = [], courses = {} }) =>
  enrollments
    .filter(
      (enrollment) =>
        enrollment.status === 'completed' || Number(enrollment.progress || 0) >= 100,
    )
    .map((enrollment) => ({
      ...enrollment,
      courseName: courses[enrollment.courseId]?.name || 'Course',
    }))
    .sort((a, b) => new Date(b.enrollmentDate || 0) - new Date(a.enrollmentDate || 0));

export const resolveActiveCourseContexts = ({ enrollments = [], courses = {} }) =>
  enrollments
    .filter((enrollment) => enrollment.status === 'active')
    .map((enrollment) => {
      const course = courses[enrollment.courseId] || {};
      const batches = course.batches || [];
      const batch = pickBatchForEnrollment(batches, enrollment);
      const resolvedBatchIndex =
        batch != null ? batches.findIndex((row) => row === batch) : -1;

      return {
        ...enrollment,
        course,
        courseName: course.name || 'Course',
        batch,
        batchName: batch?.batchId || enrollment.batchId || 'Assigned batch',
        batchIndex:
          resolvedBatchIndex >= 0
            ? resolvedBatchIndex
            : enrollment.batchIndex ?? null,
        branchId: enrollment.branchId || batch?.branchId || '',
        branchName: enrollment.branchName || batch?.branchName || '',
        branch:
          enrollment.branchName ||
          batch?.branchName ||
          batch?.branch ||
          course.branch ||
          'Branch not set',
        scheduleDays: batch?.schedule?.days || [],
        scheduleTime: batch?.schedule?.timeSlot || '',
      };
    });
